import * as THREE from "three";
import { BaseManager } from "../base/BaseManager";
import { Context } from "../core/context";
import { DBEntity } from "../core/DBEntity";
import { DbLine } from "../core/DbLine";
import { DbMesh } from "../core/DbMesh";
import { SelectionBox } from "../core/SelectionBox";
import { IndserObject } from "../core/IndserObject";
import { SelectionData } from "../core/SelectionData";
import { EventManager } from "./EventManager";
import { SelectionHelper } from "../utils/SelectionHelper";
import { ScreenCoordinateHelper } from "../utils/ScreenCoordinateHelper";
import { FreeThreeObject } from "../utils/GeometryUtils";
import { BPMathUtils } from "../utils/BPMathUtils";
import { snap, SnapModel } from "../core/SnapModel";
import { CircleAnchorPointAuxs } from "@/Dimensions/CircleAnchorPointAuxs";
import {
  Selection,
  InsertObject,
  SelectionState,
  Selection_Event,
  CommonColor,
} from "@/core/Parameters";
import { ULabel } from "@/ui/ULabel";

class SelectionManager extends BaseManager {
  private m_eventManager: EventManager = new EventManager();
  public m_raycaster: THREE.Raycaster = new THREE.Raycaster();
  private m_plane: THREE.Plane = new THREE.Plane();
  private m_isHover: boolean = true;
  private m_paceObject: THREE.Sprite | undefined;
  private m_enableSnap: boolean = false;
  public get EnableHover() {
    return this.m_isHover;
  }
  public set setEnableHover(visible: boolean) {
    this.m_isHover = visible;
  }
  public multiSelect: boolean;
  public selectionBox: SelectionBox | undefined;
  public helper1: SelectionHelper | undefined;
  public m_selection: SelectionData;
  private m_hoverData: SelectionData;
  private m_selectionState: SelectionState = SelectionState.None;
  public get SelectionState() {
    return this.m_selectionState;
  }
  private m_hovercolor: number = 0xff4addec;
  private m_selectcolor: number = 0xff4addec;
  private m_enableCtrl: boolean;
  private PlaceBeforeCallBack: ((selection: Selection) => boolean) | undefined;
  public holeStartPoint: THREE.Vector3 | undefined;
  public holeEndPoint: THREE.Vector3 | undefined;
  private m_snapModel: SnapModel;
  public drawPoints: THREE.Vector3[] = [];
  public drawlineEve: any;
  public sphereTmp: THREE.Object3D | undefined;
  public lineObj: THREE.Line | undefined;
  public HighLighting = true;
  private m_circleAnchor!: CircleAnchorPointAuxs;
  public EnableCircleAnchor: boolean = true;
  private m_scale: number = 0;
  public get Scale() {
    return this.m_scale;
  }
  private m_TextLabel: ULabel;
  private m_isRectangleCheck: boolean = true;

  constructor(context: Context) {
    super(context);
    this.m_selection = new SelectionData(context);
    this.m_hoverData = new SelectionData(
      context,
      new THREE.Color(CommonColor.Pick)
    );
    this.m_plane.set(new THREE.Vector3(0, 0, 1), 0);
    this.m_context.ViewManager.EventManager.addEventListener(
      "pointermove",
      this.onMouseMove.bind(this)
    );
    this.m_context.ViewManager.EventManager.addEventListener(
      "pointerup",
      this.onMouseUp.bind(this)
    );
    this.m_context.ViewManager.EventManager.addEventListener(
      "pointerdown",
      this.onMouseDown.bind(this)
    );
    window.addEventListener("keydown", this.keydown.bind(this));
    window.addEventListener("keyup", this.keyup.bind(this));
    this.m_context.ViewManager.Controls.addEventListener(
      "zoom",
      this.onZoom.bind(this)
    );
    this.multiSelect = false;
    this.m_enableCtrl = false;
    this.drawlineEve = undefined;
    this.sphereTmp = undefined;
    this.lineObj = undefined;
    this.m_snapModel = new SnapModel(
      this.m_context.ViewManager.m_context.SceneManager.AuxModel
    );

    this.m_TextLabel = new ULabel(
      this.m_context.ViewManager,
      this.m_context.Dom
    );
    this.m_raycaster.firstHitOnly = true;
  }

  public get EventManager() {
    return this.m_eventManager;
  }

  /**
   * 恢复默认的hover颜色
   */
  public ResetHoverColor() {
    this.m_hoverData.setColor(this.m_hovercolor, 1);
  }
  /**
   * 恢复默认的搞了颜色
   */
  public ResetSelectionColor() {
    this.m_selection.setColor(this.m_selectcolor, 1);
  }

  /**
   * 设置hover颜色
   * @param color
   * @param opacity
   */
  public SetHoverColor(color: number, opacity: number = 1) {
    this.m_hoverData.setColor(color, opacity);
  }
  /**
   * 设置高亮颜色
   * @param color
   * @param opacity
   */
  public SetSelectionColor(color: number, opacity: number = 1) {
    this.m_selection.setColor(color, opacity);
  }
  public OpenSelectBox(): void {
    this.selectionBox = new SelectionBox(
      this.m_context.ViewManager.Camera,
      this.m_context.ModelManager,
      this.m_context.SceneManager.ThreeLayer,
      this.m_context.SceneManager.SignModel
    );
    this.helper1 = new SelectionHelper(
      this.m_context.ViewManager.EventManager,
      this.m_context.Dom,
      "selectBox"
    );
  }
  public cancelSelectBox(): void {
    this.m_selectionState = SelectionState.None;
    if (this.helper1) {
      this.helper1.dispose();
    }
    this.m_context.ViewManager.Render();
  }

  private currentPoint: THREE.Vector3 = new THREE.Vector3();
  public get CurrentPoint() {
    return this.currentPoint;
  }

  public onMouseUp(event: MouseEvent) {
    if (event.button != 0) {
      return;
    }
    this.m_hoverData.clear();

    if (!this.HighLighting) return;
    switch (this.m_selectionState) {
      case SelectionState.SelectionBox:
        {
          if (this.helper1 && this.helper1.isDown) {
            this.selectionBox?.m_endPoint.set(
              (event.offsetX / this.m_context.Dom.clientWidth) * 2 - 1,
              -(event.offsetY / this.m_context.Dom.clientHeight) * 2 + 1,
              0.5
            );
            this.selectionBox!.BoxEndPoint = this.currentPoint.clone();
            if(this.m_isRectangleCheck){
              this.selectionBox?.select();
            }
            if (this.selectionBox?.Collection) {
              let selectObjects = this.m_selection.getObjects();
              for (let i = 0; i < this.selectionBox?.Collection.length; i++) {
                let entity = this.selectionBox?.Collection[i];
                if (selectObjects.includes(entity)) {
                  this.selectionBox?.Collection.splice(i, 1);
                  i--;
                }
              }

              this.m_selection.addObjects(this.selectionBox?.Collection, true);
              this.selectionBox.SelectType = "include";
            }
            this.m_eventManager.dispatchEvent(Selection_Event.SelectionBox, {
              box: this.selectionBox?.Box(),
              entity: this.m_selection.getObjects(),
            });
          }
          this.m_context.ViewManager.Controls.EnablePan = true;
        }

        break;
      case SelectionState.Place:
        {
          let p = this.m_context.ViewManager.Controls.DownPointXY;
          let u = new THREE.Vector2(event.offsetX, event.offsetY);
          if (p.distanceTo(u) > 4)
            //鼠标滑动超过5个像素时，视为移动不进行放置
            return;

          if (this.m_paceObject) {
            let intersectObject = undefined;
            this.GetInsertObject(event.offsetX, event.offsetY, (obj) => {
              intersectObject = obj.obejct[0];
            });
            let target = this.GetPointByScreenXY(event.offsetX, event.offsetY);
            if (
              this.PlaceBeforeCallBack == undefined ||
              this.PlaceBeforeCallBack({
                location: target,
                entity: intersectObject,
              })
            ) {
              let placeObejct = this.m_paceObject.clone();
              placeObejct.position.copy(target);
              this.m_context.SceneManager.SignModel.children.push(placeObejct);
              let select: Selection = {
                location: {
                  x: Number(target.x),
                  y: Number(target.y),
                  z: Number(target.z),
                },
                entity: placeObejct,
              };
              this.m_eventManager.dispatchEvent(Selection_Event.Place, {
                place: select,
                next: this.m_paceObject,
              });
              placeObejct.userData.id = placeObejct.id;
              this.m_context.ViewManager.Render();
            }
          }
        }
        break;
      case SelectionState.None:
        {
          if (
            this.m_isHover ||
            this.m_selectionState.toString() == SelectionState.Place
          ) {
            let isInsert = false;
            let startLinePoint = this.GetPointByScreenXY(
              event.offsetX,
              event.offsetY
            );
            let PickObj = {
              pickPosition: new THREE.Vector3(),
              isPick: false,
            };
            this.GetInsertObject(event.offsetX, event.offsetY, (object) => {
              if (object.adjust) {
                startLinePoint = new THREE.Vector3(
                  object.adjust!.x,
                  object.adjust!.y,
                  object.adjust!.z
                );
                PickObj.pickPosition = startLinePoint;
                PickObj.isPick = true;
              }
              this.m_eventManager.dispatchEvent(
                Selection_Event.inserts,
                object
              );
              isInsert = true;
              if (this.HighLighting) {
                this.m_hoverData.addObjects(object.obejct);
              }
            });
            if (!isInsert) {
              let pos = this.GetPointByScreenXY(event.offsetX, event.offsetY);
              this.m_eventManager.dispatchEvent(
                Selection_Event.inserts,
                new IndserObject(pos, 0, "", [])
              );
            }
          }
        }
        break;
      case SelectionState.Pick:
        {
          this.GetInsertObject(event.offsetX, event.offsetY, (obj) => {
            let object = obj.obejct[0];
            if (object instanceof DBEntity) {
              // if (!object?.extend_id) {
              //   return;
              // }
            }
            let select: Selection = {
              location: {
                x: Number(obj.location.x),
                y: Number(obj.location.y),
                z: Number(obj.location.z),
              },
              entity: obj.obejct[0],
              code: obj.code,
            };
            this.m_eventManager.dispatchEvent(Selection_Event.Pick, select);
          });
        }
        break;
      default: {
        // let p = this.m_context.ViewManager.Controls.DownPointXY;
        // let u = new THREE.Vector2(event.offsetX, event.offsetY);
        // if (p.distanceTo(u) > 4)
        //   //鼠标滑动超过5个像素时，视为移动不进行放置
        //   return;
        // this.GetInsertObject(event.offsetX, event.offsetY, (obj) => {
        //   if (this.m_enableCtrl) {
        //     // let rtValk = this.m_selection.clearObj(obj.obejct[0]);
        //     // console.log(rtValk);
        //     let select: Selection = {
        //       location: {
        //         x: Number(obj.location.x),
        //         y: Number(obj.location.y),
        //         z: Number(obj.location.z),
        //       },
        //       entity: obj.obejct[0],
        //     };
        //     // if (!rtValk) {
        //     //   this.m_selection.addObjects(...obj.obejct);
        //     //   this.m_eventManager.dispatchEvent(Selection_Event.Pick, select);
        //     // } else {
        //     //   this.m_eventManager.dispatchEvent(Selection_Event.Pick, select);
        //     // }
        //     this.m_eventManager.dispatchEvent(Selection_Event.Pick, select);
        //   } else {
        //     let select: Selection = {
        //       location: {
        //         x: Number(obj.location.x),
        //         y: Number(obj.location.y),
        //         z: Number(obj.location.z),
        //       },
        //       entity: obj.obejct[0],
        //       code: obj.code,
        //     };
        //     // let isCloud =
        //     //   this.m_context.ToolsManager.judgeSelectDataIsIncludeSomeTypeData(
        //     //     obj.obejct[0] as DBEntity
        //     //   );
        //     // console.log(isCloud);
        //     // this.m_selection.clear();
        //     // if (!isCloud) {
        //     //   this.m_selection.addObjects(...obj.obejct);
        //     // }
        //     this.m_eventManager.dispatchEvent(Selection_Event.Pick, select);
        //   }
        // });
      }
    }
  }

  public get HoverData() {
    return this.m_hoverData.getObjects();
  }

  public onMouseDown(event: MouseEvent) {
    if (!this.m_circleAnchor) {
      this.m_circleAnchor = new CircleAnchorPointAuxs(
        this.m_context.ViewManager,
        this.m_context.SceneManager.AuxModel
      );
    }
    if (event.button != 0) {
      return;
    }
    this.circleAuxPoint.length = 0;
    //框选
    if (this.m_selectionState == SelectionState.SelectionBox) {
      this.m_context.ViewManager.Controls.EnablePan = false;
      this.selectionBox!.BoxStartPoint = this.currentPoint.clone();
      this.selectionBox?.m_startPoint.set(
        (event.offsetX / this.m_context.Dom.clientWidth) * 2 - 1,
        -(event.offsetY / this.m_context.Dom.clientHeight) * 2 + 1,
        0.5
      );
      if (this.selectionBox?.frontSelectBox) {
        let scale =
          Math.pow(this.m_context.ViewManager.ScreenModelScale, 1) * 10;
        this.selectionBox?.frontSelectBox.scale.set(scale, scale, 1);
        this.selectionBox?.reverseSelectBox.scale.set(scale, scale, 1);
        this.mouseStartPoint = this.GetPointByScreenXY(
          event.offsetX,
          event.offsetY
        );
      }
    }
    //点选
    else if (this.m_selectionState == SelectionState.Pick) {
    } else if (this.m_selectionState == SelectionState.Place) {
    }
    //不选
    else if (this.m_selectionState == SelectionState.None) {
    }
  }

  private mouseMovePosition: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private mouseStartPoint: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private circleAuxPoint: THREE.Vector3[] = [];

  public onMouseMove(event: MouseEvent, endPoint?: THREE.Vector3) {
    this.m_hoverData.clear();
    this.currentPoint = this.GetPointByScreenXY(event.offsetX, event.offsetY);
    if (event.buttons == 4) {
      this.m_context.ViewManager.Controls.EnablePan = true;
    } else {
      this.m_context.ViewManager.Controls.EnablePan = false;
    }

    this.mouseMovePosition = this.GetPointByScreenXY(
      event.offsetX,
      event.offsetY
    );
    this.m_scale = ScreenCoordinateHelper.GetScale(
      this.m_context.ViewManager.Camera as THREE.PerspectiveCamera,
      this.m_context.Dom.offsetHeight
    );
    // this.divtag.style.display='none'
    // clearInterval(this.setInterEve)
    if (this.m_context.ViewManager.Controls.IsPan) {
      if (this.m_selectionState == SelectionState.SelectionBox) {
        let dir = this.mouseMovePosition
          .clone()
          .sub(this.mouseStartPoint)
          .normalize();
        let signPosition = new THREE.Vector3();

        if (this.selectionBox?.frontSelectBox) {
          if (event.buttons !== 1) {
            return;
          }
          if (dir.y > 0) {
            this.helper1!.element.className = "selectBoxReverse";
            signPosition = new THREE.Vector3(
              this.mouseMovePosition.x - this.m_scale * 10,
              this.mouseMovePosition.y + this.m_scale * 10,
              0
            );
            if (dir.x > 0) {
              signPosition = new THREE.Vector3(
                this.mouseMovePosition.x + this.m_scale * 10,
                this.mouseMovePosition.y + this.m_scale * 10,
                0
              );
            }
            this.selectionBox.frontSelectBox.visible = false;
            this.selectionBox.reverseSelectBox.visible = true;
            this.selectionBox?.reverseSelectBox.position.copy(signPosition);
          } else {
            this.helper1!.element.className = "selectBox";
            signPosition = new THREE.Vector3(
              this.mouseMovePosition.x - this.m_scale * 10,
              this.mouseMovePosition.y - this.m_scale * 10,
              0
            );
            if (dir.x > 0) {
              signPosition = new THREE.Vector3(
                this.mouseMovePosition.x + this.m_scale * 10,
                this.mouseMovePosition.y - this.m_scale * 10,
                0
              );
            }
            this.selectionBox.frontSelectBox.visible = true;
            this.selectionBox.reverseSelectBox.visible = false;
            this.selectionBox?.frontSelectBox.position.copy(signPosition);
          }
        }
      }

      return;
    }
    switch (this.m_selectionState) {
      case SelectionState.None:
        {
          if (
            this.m_isHover ||
            this.m_selectionState.toString() == SelectionState.Place
          ) {
            let isInsert = false;
            this.GetInsertObject(event.offsetX, event.offsetY, (object) => {
              let resBool = true;
              if (object.obejct.length > 0) {
                object.obejct.forEach((item: any) => {
                  if (
                    item instanceof THREE.Line &&
                    item.name == "ProtectRadiusProject"
                  ) {
                    resBool = false;
                  }
                });
              }
              if (!resBool) {
                object.location = this.GetPointByScreenXY(
                  event.offsetX,
                  event.offsetY
                );
              }

              this.m_eventManager.dispatchEvent(
                Selection_Event.inserts,
                object
              );
              isInsert = true;
              if (this.HighLighting) {
                if (object.obejct?.length) {
                  this.m_hoverData.addObjects(object.obejct);
                  // let insertObj = object.obejct[0];
                  // if (insertObj instanceof DbLine) {
                  //   let result =
                  //     this.m_context.ToolsManager.CheckIsCircle(insertObj);
                  //   if (result && result.isCircle) {
                  //     if (insertObj.sphere) {
                  //       let center = new THREE.Vector3(
                  //         insertObj.sphere.center.x,
                  //         insertObj.sphere.center.y,
                  //         0
                  //       );
                  //       let isCloud = false;
                  //       if (this.circleAuxPoint.length) {
                  //         for (let i = 0; i < this.circleAuxPoint.length; i++) {
                  //           let circleAuxPoint = this.circleAuxPoint[i];
                  //           if (circleAuxPoint.equals(center)) {
                  //             isCloud = true;
                  //           }
                  //         }
                  //       }
                  //       if (!isCloud) {
                  //         this.circleAuxPoint.push(center);
                  //       }
                  //     }
                  //   }
                  // }
                }
              }
            });
            if (!isInsert) {
              let pos = this.GetPointByScreenXY(event.offsetX, event.offsetY);
              this.m_eventManager.dispatchEvent(
                Selection_Event.inserts,
                new IndserObject(pos, 0, "", [])
              );
            }
          }

          if (this.m_circleAnchor && !this.EnableCircleAnchor) {
            this.m_circleAnchor.SetPoint(
              this.circleAuxPoint,
              this.m_context.ViewManager.ScreenModelScale
            );
          }
        }
        break;
      case SelectionState.Place:
        {
          if (this.m_paceObject) {
            let target = this.GetPointByScreenXY(event.offsetX, event.offsetY);
            this.m_paceObject.position.copy(target);
          }
        }
        break;
      case SelectionState.Pick:
        {
          let TextLabelLocation = new THREE.Vector3();
          TextLabelLocation.x = this.currentPoint.x + this.m_scale * 45;
          TextLabelLocation.y = this.currentPoint.y - this.m_scale * 10;
          if (this.m_isSingle) {
            this.m_TextLabel.Visibility = false;
          } else {
            this.m_TextLabel.Visibility = true;
            this.m_TextLabel.SetVaule(
              "按住Ctrl键将可以拾取多个对象",
              TextLabelLocation,
              new THREE.Vector3(1, 0, 0)
            );
          }

          if (!this.m_isHover) return;
          let isInsert = false;
          this.GetInsertObject(event.offsetX, event.offsetY, (object) => {
            isInsert = true;
            this.m_hoverData.addObjects(object.obejct);
          });
          if (!isInsert) {
            this.m_hoverData.clear();
          }
        }
        break;
      case SelectionState.SelectionBox: {
        if (this.helper1 && this.helper1.isDown) {
          this.selectionBox?.m_endPoint.set(
            (event.offsetX / this.m_context.Dom.clientWidth) * 2 - 1,
            -(event.offsetY / this.m_context.Dom.clientHeight) * 2 + 1,
            0.5
          );
        }
      }
    }
  }

  public onZoom(event: WheelEvent) {
    this.circleAuxPoint.length = 0;
  }

  private keydown(event: any) {
    this.circleAuxPoint.length = 0;
    if (event.keyCode == 17) {
      this.m_enableCtrl = true;
    }
  }
  private keyup(event: any) {
    this.m_enableCtrl = false;
  }

  public Tol: number = 0;

  public GetInsertObject(
    sx: number,
    sy: number,
    callback: (param: IndserObject) => void
  ) {
    let camera = this.m_context.ViewManager.Camera;
    // let x = (sx / this.m_context.Dom.offsetWidth) * 2 - 1;
    // let y = -(sy / this.m_context.Dom.offsetHeight) * 2 + 1;
    let dpi = this.getDpi(sx, sy);
    // this.m_raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
    let ray = this.m_raycaster.ray;
    // let position = new THREE.Vector3();
    // camera.getWorldPosition(position).z;
    //@ts-ignore
    // let halfAngle = camera.fov * 0.5 * (Math.PI / 180);
    // let L = Math.tan(halfAngle) * camera.position.z * 2;
    // let P = this.m_context.Dom.offsetHeight;
    // let P2 = L / P;
    let P2 = dpi.p;
    let mindis = dpi.p * 5;
    if (
      this.m_raycaster.intersectObject(this.m_context.SceneManager.MaskLayer)
        .length > 0
    ) {
      return;
    }
    let array: IndserObject[] = [];
    this.m_context.ModelManager.GetInsertObject(ray, mindis, (params) => {
      array.push(params);
      return this.m_enableSnap;
    });
    //抛出snapFilter事件，在此处可加入需要捕捉的点位
    this.m_eventManager.dispatchEvent(Selection_Event.snapFilter, {
      ray: ray,
      array: array,
      tol: mindis,
    });
    let polyLines = this.m_context.ToolsManager.RayIntersectPolygonLine(
      this.m_raycaster,
      mindis
    );
    if (polyLines.length) {
      array.push(...polyLines);
    }
    if (array.length > 0) {
      if (this.m_enableSnap) {
        //返回最佳捕捉点
        let posType: string[] = array.map((item) => {
          return item.position;
        });
        let posArr = array.filter((item) => {
          if (
            posType.includes("start") ||
            posType.includes("end") ||
            posType.includes("cross")
          ) {
            if (!item.position.includes("aux")) {
              return true;
            }
          } else {
            return true;
          }
        });

        let min = BPMathUtils.min(posArr, (item) => item.distance);

        if (min) {
          callback(min);
          let posistion = min.adjust
            ? new THREE.Vector3(min.adjust.x, min.adjust.y, min.adjust.z)
            : new THREE.Vector3(min.location.x, min.location.y, min.location.z);
          let fun: { [key: string]: () => void } = {
            start: () => {
              this.m_snapModel.set(snap.end, posistion, P2);
            },
            end: () => {
              this.m_snapModel.set(snap.end, posistion, P2);
            },
            center: () => {
              this.m_snapModel.set(snap.center, posistion, P2);
            },
            insert: () => {
              this.m_snapModel.set(snap.insert, posistion, P2);
            },
            cross: () => {
              this.m_snapModel.set(snap.cross, posistion, P2);
            },
            vertical: () => {
              this.m_snapModel.set(snap.vertical, posistion, P2);
            },
            auxOrthogonal: () => {
              this.m_snapModel.set(snap.other, posistion, P2);
            },
            auxVertical: () => {
              this.m_snapModel.set(snap.other, posistion, P2);
            },
          };
          if (fun[min.position]) {
            fun[min.position]();
          } else {
            this.m_snapModel.set(snap.none, new THREE.Vector3(0, 0, 0), P2);
          }
        }
      } else {
        callback(array[0]);
        this.m_snapModel.set(snap.none, new THREE.Vector3(0, 0, 0), P2);
        // this.m_snapModel.set(snap.cross, new THREE.Vector3(array[0].adjust!.x, array[0].adjust!.y, array[0].adjust!.z), P2);
      }
      return;
    } else {
      this.m_snapModel.set(snap.none, new THREE.Vector3(0, 0, 0), P2);
    }
    let insts = this.m_raycaster.intersectObject(
      this.m_context.SceneManager.SignModel
    );
    // console.log('vvvvvvvvvvvvvvvvvvvvvvv')
    // console.log(insts)
    // console.log('vvvvvvvvvvvvvvvvvvvvvvv')
    if (insts.length > 0) {
      let obj = insts[0].object;
      let location = {
        x: Number(insts[0].point.x),
        y: Number(insts[0].point.y),
        z: Number(insts[0].point.z),
      };
      callback(new IndserObject(location, insts[0].distance, "", [obj]));
      return;
    }
    let temp: THREE.Intersection[] = [];
    if (
      !this.m_context.SceneManager.ThreeLayer.userData.disEnable &&
      this.HighLighting
    ) {
      // insts = this.m_raycaster.intersectObject(
      //   this.m_context.SceneManager.ThreeLayer
      // );
      let instsMesh = this.m_context.ToolsManager.RayIntersectPolygon(
        this.m_raycaster
      );
      instsMesh.forEach((item) => {
        if (item.object.visible && !(item.object instanceof THREE.LineSegments))
          temp.push(item);
      });
    }
    insts = temp;
    if (insts.length > 0) {
      let objs = insts.map((inst) => {
        return inst.object;
      });
      let location = {
        x: Number(insts[0].point.x),
        y: Number(insts[0].point.y),
        z: Number(insts[0].point.z),
      };
      callback(new IndserObject(location, insts[0].distance, "", objs));
      return;
    }
  }
  private getDpi(sx: number, sy: number) {
    let camera = this.m_context.ViewManager.Camera;
    let x = (sx / this.m_context.Dom.offsetWidth) * 2 - 1;
    let y = -(sy / this.m_context.Dom.offsetHeight) * 2 + 1;
    this.m_raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
    let ray = this.m_raycaster.ray;
    let position = new THREE.Vector3();
    camera.getWorldPosition(position).z;
    let array: InsertObject[] = [];
    let p = 1;
    //@ts-ignore
    if (camera instanceof THREE.PerspectiveCamera) {
      let halfAngle = camera.fov * 0.5 * (Math.PI / 180); //视角的一半
      let L = Math.tan(halfAngle) * camera.position.z * 2; //视角的一半对应的长度
      let PL = this.m_context.Dom.offsetHeight; // 视口的高度
      p = L / PL; // 一个像素对应的长度
    } else if (camera instanceof THREE.OrthographicCamera) {
      p = (camera.top - camera.bottom) / this.m_context.Dom.offsetHeight;
    }
    return { p, ray };
  }

  public get CameraRay() {
    return this.m_raycaster;
  }

  /**
   *
   * @param hoverCallback hover回调
   * @param pickCallback 拾取回调，返回ture 继续拾取， false 停止拾取
   * @param layerId
   */
  private selectionDataArr: any = [];
  public selectData!: Selection | Selection[];
  private m_isSingle: boolean = false;
  private pick!: (selection: Selection, isSingle: boolean) => void;
  public Pick(
    pickCallback: (slection: Selection | Selection[]) => boolean,
    isSingle: boolean = false
  ) {
    this.m_isSingle = isSingle;
    this.m_context.Dom.style.cursor = "crosshair";
    this.m_TextLabel.Visibility = true;
    this.pick = (selection: Selection) => {
      let isCloud =
        this.m_context.ToolsManager.judgeSelectDataIsIncludeSomeTypeData(
          selection.entity as DBEntity
        );
      if (selection.entity) {
        if (!this.m_enableCtrl || isSingle) {
          this.m_selection.clear();
        }
        if (!isCloud) {
          this.m_selection.addObjects([selection.entity], true);
        }
      }

      pickCallback(selection);
    };
    this.m_eventManager.addEventListener(Selection_Event.Pick, this.pick);
    this.m_selectionState = SelectionState.Pick;
  }

  // 选中全部区域
  public SelectAll() {
    let selectAll: any[] = [];
    let { PolygonDatas, PMesh } = this.m_context.ViewManager;
    if (!PMesh.visible) return [];
    for (const value of PolygonDatas.values()) {
      selectAll.push(value.shapeMesh.clone());
    }
    this.ClearSelected();
    this.m_selection.addObjects(selectAll);
    return selectAll.map((item) => item.userData.id);
  }

  public PassLayerNameSelectAll(layerNames: string[]) {
    let selectAll =
      this.m_context.ModelManager.PassLayerNameGetData(layerNames);
    this.ClearSelected();
    this.m_selection.addObjects(selectAll);
  }

  /**
   * 结束选取操作
   */
  public CancelPick() {
    this.m_TextLabel.Visibility = false;
    this.m_eventManager.ClearListener(Selection_Event.Pick);
    this.selectionDataArr.length = 0;
    this.m_selectionState = SelectionState.None;
    this.m_context.Dom.style.cursor = "default";
    this.m_enableCtrl = false;
    this.multiSelect = false;
    this.m_context.ViewManager.Render();
    this.m_enableSnap = false;
  }

  /**
   * 框选元素
   * @param callback 框选回调，返回ture 继续拾取， false 停止拾取
   */
  public PickRectangle(
    callback: (seleceData: {
      box: THREE.Box3;
      entity: DBEntity[];
      isFrontSelectionBox: boolean;
    }) => boolean,
    isRectangleCheck: boolean = true
  ) {
    this.m_context.Dom.style.cursor = "crosshair";
    this.m_isRectangleCheck = isRectangleCheck;
    if (this.HighLighting) {
      this.m_hoverData.clear();
    }
    let selectBox = (seleceData: { box: THREE.Box3; entity: DBEntity[] }) => {
      let dir = this.mouseMovePosition
        .clone()
        .sub(this.mouseStartPoint)
        .normalize();

      let isFrontSelectionBox = true;
      if (dir.y > 0) {
        isFrontSelectionBox = false;
      }
      if (
        !callback({
          box: seleceData.box,
          entity: seleceData.entity,
          isFrontSelectionBox,
        })
      ) {
        this.m_eventManager.removeEventListener(
          Selection_Event.SelectionBox,
          selectBox
        );
        this.m_selectionState = SelectionState.None;
        this.m_context.Dom.style.cursor = "default";
        this.cancelSelectBox();
        this.m_isRectangleCheck = true;
      }
    };

    this.m_eventManager.addEventListener(
      Selection_Event.SelectionBox,
      selectBox
    );
    this.m_selectionState = SelectionState.SelectionBox;
    this.OpenSelectBox();
    return selectBox;
  }

  public PickRectangleEnd() {
    this.m_eventManager.ClearListener(Selection_Event.SelectionBox);
    this.m_selectionState = SelectionState.None;
    this.cancelSelectBox();
    this.m_isRectangleCheck = true;
    this.m_context.Dom.style.cursor = "default";
  }

  public get PlaceObject() {
    return this.m_paceObject;
  }

  public ClearSelected(): void {
    this.m_selection.clear();
  }

  public GetSelected(): (DBEntity | THREE.Object3D)[] {
    return this.m_selection.getObjects();
  }

  public RemoveSelect(entity: DBEntity | THREE.Object3D) {
    this.m_selection.clearObj(entity);
  }

  public GetPointByScreenXY(x: number, y: number) {
    const x1 = (x / this.m_context.Dom.clientWidth) * 2 - 1;
    const y1 = -(y / this.m_context.Dom.clientHeight) * 2 + 1;
    this.m_raycaster.setFromCamera(
      new THREE.Vector2(x1, y1),
      this.m_context.ViewManager.Camera
    );

    let target = new THREE.Vector3();
    this.m_raycaster.ray.intersectPlane(
      this.m_context.ViewManager.m_controls.m_plane,
      target
    );
    return target;
  }

  public GetScreenXYByPoint(point: THREE.Vector3) {
    let p = new THREE.Vector3(point.x, point.y, point.z);
    let vector = p.project(
      this.m_context.ViewManager.Camera as THREE.PerspectiveCamera
    );
    vector.x = ((vector.x + 1) / 2) * this.m_context.Dom.clientWidth;
    vector.y = -((vector.y - 1) / 2) * this.m_context.Dom.clientHeight;
    return vector;
  }

  /**
   * @param entity 设置选择项，并且高亮
   */
  public SetSelected(...entity: (DBEntity | THREE.Object3D)[]) {
    this.m_selection.addObjects(entity);
    this.m_context.ViewManager.Render();
  }

  public EnableSnap(enable: boolean) {
    this.m_enableSnap = enable;
  }
  public get getEnableSnap() {
    return this.m_enableSnap;
  }
}

export { SelectionManager, Selection_Event };
